import { Stack, Typography } from '@mui/material';
import { Banknote, CalendarClock, CreditCard, Globe, Mail } from 'lucide-react';
import { ReactNode } from 'react';
import { SettingsSectionRow } from 'src/components/SettingsSection';
import { getCountryLabel } from './constants';
import {
  formatBankAccountLabel,
  getBankAccountStatusPresentation,
  StripeConnectedBankAccount,
} from './stripe-account-display';
import { StatusBadge } from './styled';

const RowValue = ({ children }: { children: ReactNode }) => (
  <Typography variant="body2" fontWeight={500} color="custom.OV.sys.onSurface">
    {children}
  </Typography>
);

interface StripeConnectedRowsProps {
  bankAccount?: StripeConnectedBankAccount | null;
  country?: string;
  email?: string;
  payoutSchedule?: string;
  isFullySetup: boolean;
}

export const StripeConnectedRows = ({
  bankAccount,
  country,
  email,
  payoutSchedule,
  isFullySetup,
}: StripeConnectedRowsProps) => {
  const bankAccountLabel = formatBankAccountLabel(bankAccount);
  const bankAccountStatus = getBankAccountStatusPresentation(bankAccount?.status);

  return (
    <>
      <SettingsSectionRow
        icon={<CreditCard />}
        title="Account status"
        secondary={
          isFullySetup
            ? 'Payments are deposited directly to your bank account.'
            : 'Onboarding is incomplete, so charges and payouts are on hold.'
        }
        control={
          <StatusBadge status={isFullySetup ? 'success' : 'warning'}>
            {isFullySetup ? 'Active' : 'Setup incomplete'}
          </StatusBadge>
        }
        controlAlign="center"
        mobileControlLayout="below"
      />

      <SettingsSectionRow
        icon={<Globe />}
        title="Country"
        control={<RowValue>{getCountryLabel(country)}</RowValue>}
        controlAlign="center"
      />

      {isFullySetup && email ? (
        <SettingsSectionRow
          icon={<Mail />}
          title="Account email"
          control={<RowValue>{email}</RowValue>}
          controlAlign="center"
          mobileControlLayout="below"
        />
      ) : null}

      {isFullySetup && bankAccountLabel ? (
        <SettingsSectionRow
          icon={<Banknote />}
          title="Bank account"
          control={
            <Stack direction="row" alignItems="center" gap={1.5}>
              <RowValue>{bankAccountLabel}</RowValue>
              {bankAccountStatus ? (
                <StatusBadge status={bankAccountStatus.tone}>
                  {bankAccountStatus.label}
                </StatusBadge>
              ) : null}
            </Stack>
          }
          controlAlign="center"
          mobileControlLayout="below"
        />
      ) : null}

      {isFullySetup && payoutSchedule ? (
        <SettingsSectionRow
          icon={<CalendarClock />}
          title="Payout schedule"
          control={<RowValue>{payoutSchedule}</RowValue>}
          controlAlign="center"
          mobileControlLayout="below"
        />
      ) : null}
    </>
  );
};
